import { ColumnDefinition, ContentCard, RowDefinition, Table } from '@indigo-ag/component-module';
import { PriorLoan } from '@indigo-ag/schema';
import i18next from 'i18next';
import React from 'react';

import { CURRENCY_SYMBOL, currency, isPresent, parseNumberToLocale } from 'shared';
import { getCreditAssignmentStatusTranslation, getPaymentStatusTranslation } from './utils/decisionSheet.util';

const EMPTY_VALUE = '-';

enum ColumnId {
  USED_LIMIT = 'USED_LIMIT',
  COLLATERAL = 'COLLATERAL',
  CESSION_DELIVERED = 'CESSION_DELIVERED',
  TRADING = 'TRADING',
  VALUE = 'VALUE',
  PAYMENT_STATUS = 'PAYMENT_STATUS'
}

interface RowShape {
  campaignName?: string;
  cessionDelivered?: string;
  collateralType?: string;
  creditAssignmentValue?: number;
  id: string;
  paymentStatus?: string;
  trading?: string;
  usedLimit?: number;
}

const columnDefinition: ColumnDefinition<RowShape, ColumnId>[] = [
  {
    id: ColumnId.USED_LIMIT,
    header: i18next.t('creditApprovalHistory_usedLimit'),
    cell: ({ row: { usedLimit }, SimpleCell }) => (
      <SimpleCell
        label={
          usedLimit
            ? currency.inputMaskCurrency(usedLimit / 1000000, {
                useComaForDecimals: true,
                currencySymbol: CURRENCY_SYMBOL.BR
              })
            : EMPTY_VALUE
        }
      />
    )
  },
  {
    id: ColumnId.COLLATERAL,
    header: i18next.t('creditApprovalHistory_collateralType'),
    cell: ({ row: { collateralType }, SimpleCell }) => (
      <SimpleCell label={collateralType ? i18next.t(collateralType.valueOf()) : EMPTY_VALUE} />
    )
  },
  {
    id: ColumnId.CESSION_DELIVERED,
    header: i18next.t('creditApprovalHistory_cessionDelivered'),
    cell: ({ row: { cessionDelivered }, SimpleCell }) => <SimpleCell label={cessionDelivered ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.TRADING,
    header: i18next.t('creditApprovalHistory_trading'),
    cell: ({ row: { trading }, SimpleCell }) => <SimpleCell label={trading ?? EMPTY_VALUE} />
  },
  {
    id: ColumnId.VALUE,
    header: i18next.t('creditApprovalHistory_value'),
    cell: ({ row: { creditAssignmentValue }, SimpleCell }) => (
      <SimpleCell label={creditAssignmentValue !== null ? parseNumberToLocale(creditAssignmentValue) : EMPTY_VALUE} />
    )
  },
  {
    id: ColumnId.PAYMENT_STATUS,
    header: i18next.t('creditApprovalHistory_paymentStatus'),
    cell: ({ row: { paymentStatus }, SimpleCell }) => <SimpleCell label={paymentStatus ?? EMPTY_VALUE} />
  }
];

interface Props {
  priorLoans?: PriorLoan[] | null;
}

export const CreditApprovalHistory = ({ priorLoans }: Props) => {
  const rowDefinition: RowDefinition<RowShape, ColumnId> = { rowId: ({ row: { id } }) => id };
  const tableData =
    priorLoans?.map(
      (
        {
          campaignName,
          collateralType,
          creditAssignmentStatus,
          creditAssignmentValue,
          paymentStatus,
          tradingCompany,
          usedLimit
        },
        index
      ) => ({
        id: index.toString(),
        campaignName,
        collateralType,
        cessionDelivered: getCreditAssignmentStatusTranslation(creditAssignmentStatus),
        paymentStatus: getPaymentStatusTranslation(paymentStatus),
        creditAssignmentValue,
        trading: tradingCompany,
        usedLimit
      })
    ) ?? [];

  const table = (
    <Table
      label={i18next.t('creditApprovalHistory')}
      error={!isPresent(priorLoans)}
      columnDefinition={columnDefinition}
      rowDefinition={rowDefinition}
      data={tableData}
      groupBy={({ row: { campaignName } }) => campaignName ?? ''}
    />
  );

  return (
    <ContentCard titleText={i18next.t('creditApprovalHistory')}>
      {!isPresent(priorLoans) || priorLoans?.length ? table : i18next.t('noResultsFound')}
    </ContentCard>
  );
};
